<script setup lang="ts">
import UIPane from '@/components/ui/UIPane.vue'
import { setFocusTrap } from '@/composables/useFocusTrap'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

defineProps<{
  /** Accessible name for the dialog */
  label?: string
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const fullScreenModal = ref<HTMLDivElement | undefined>()
let lastFocusedElement: HTMLElement | null = null

// Trap focus for a11y
const handleKeydown = (e) => {
  if (e.key == 'Escape') {
    emit('close')
  } else if (setFocusTrap(e, [fullScreenModal.value])) {
    // focus trap handled the event
  }
}

// Teleported to <body>, so marking app inert traps focus inside the modal.
const appRoot = () => document.getElementById('app')

onMounted(async () => {
  lastFocusedElement = document.activeElement as HTMLElement | null
  appRoot()?.setAttribute('inert', '')
  await nextTick()
  fullScreenModal.value?.focus()
})

onBeforeUnmount(() => {
  appRoot()?.removeAttribute('inert')
  lastFocusedElement?.focus()
})
</script>

<template>
  <div>
    <Teleport to="body">
      <div
        ref="fullScreenModal"
        v-global-keydown="handleKeydown"
        role="dialog"
        aria-modal="true"
        :aria-label="label"
        tabindex="-1"
        class="outline-none"
      >
        <div
          class="z-50 fixed inset-0 bg-black opacity-20"
          @click="$emit('close')"
        ></div>
        <UIPane
          class="z-51 fixed inset-0 sm:inset-[10px] lg:inset-5 flex flex-col"
          rounding="rounded-none sm:rounded"
        >
          <slot></slot>
        </UIPane>
      </div>
    </Teleport>
  </div>
</template>
